import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAgencyPitches,
  getAgencyAgents,
  getAgencyPlayers,
} from "../../services/api";
import { searchService } from "../../services/api";
import { toast } from "react-hot-toast";
import { useDebounce } from "../useDebounce";

const buildPitchParams = (filters, agentsList) => {
  const params = {
    limit: 20,
    page: filters.page || 1,
  };

  if (!filters.allAgentsSelected && filters.selectedAgents.length > 0) {
    const selectedAgent = agentsList.find(
      (agent) => agent.id === filters.selectedAgents[0]
    );
    if (selectedAgent?.account_id) {
      params.account_id = selectedAgent.account_id;
    }
  }

  if (!filters.allPositionsSelected && filters.selectedPositions.length > 0) {
    params.position = filters.selectedPositions[0];
  }

  if (filters.search) {
    params.search = filters.search;
  }

  if (filters.sort) {
    params.sort = filters.sort;
  }

  return params;
};

const normalizePitchesResponse = (response, currentPage) => {
  const defaultPagination = {
    count: 0,
    next: null,
    previous: null,
    currentPage: currentPage || 1,
  };

  if (!response?.data) {
    return { items: [], pagination: defaultPagination };
  }

  if (response.data.results?.data) {
    return {
      items: response.data.results.data,
      pagination: {
        count: response.data.count || 0,
        next: response.data.next,
        previous: response.data.previous,
        currentPage: currentPage || 1,
      },
    };
  }

  if (Array.isArray(response.data.data)) {
    return {
      items: response.data.data,
      pagination: {
        ...defaultPagination,
        count: response.data.data.length,
      },
    };
  }

  return { items: [], pagination: defaultPagination };
};

const useAgencyPitches = () => {
  const navigate = useNavigate();

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    sort: "latest",
    allAgentsSelected: true,
    selectedAgents: [],
    allPositionsSelected: true,
    selectedPositions: [],
    page: 1,
  });

  // UI states
  const [showAgentFilter, setShowAgentFilter] = useState(false);
  const [showPositionFilter, setShowPositionFilter] = useState(false);

  // Data states
  const [pitches, setPitches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [positions, setPositions] = useState([]);
  const [pitchesLoading, setPitchesLoading] = useState(false);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });

  // Other states
  const [exportingData, setExportingData] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [hasSearchInteracted, setHasSearchInteracted] = useState(false);

  const debouncedSearch = useDebounce(filters.search.trim(), 400);

  // Fetch pitches
  const fetchPitches = useCallback(
    async (activeFilters, { showLoader = true, agentsSource } = {}) => {
      const resolvedAgents = Array.isArray(agentsSource)
        ? agentsSource
        : agents;
      if (showLoader) {
        setPitchesLoading(true);
      }

      try {
        const params = buildPitchParams(activeFilters, resolvedAgents);
        const pitchesRes = await getAgencyPitches(params);
        const { items, pagination: paginationData } = normalizePitchesResponse(
          pitchesRes,
          activeFilters.page || 1
        );

        setPitches(items);
        setPagination(paginationData);
      } catch (error) {
        console.error("Failed to fetch pitches:", error);
        setPitches([]);
        setPagination((prev) => ({
          ...prev,
          count: 0,
          next: null,
          previous: null,
          currentPage: activeFilters.page || 1,
        }));
      } finally {
        setPitchesLoading(false);
      }
    },
    [agents]
  );


  // Initial data fetch
  useEffect(() => {
    let isCancelled = false;

    const fetchInitialData = async () => {
      setPitchesLoading(true);

      try {
        const [agentsRes, positionsRes] = await Promise.all([
          getAgencyAgents({ type: "all" }),
          searchService.getPositions(),
        ]);

        if (isCancelled) {
          return;
        }

        const agentsData = agentsRes.data?.results?.data || {};
        const allAgents = [
          ...(Array.isArray(agentsData.licensed) ? agentsData.licensed : []),
          ...(Array.isArray(agentsData.unlicensed)
            ? agentsData.unlicensed
            : []),
        ];

        console.log("positionsRes positionsRes:", positionsRes);

        const positionsData =
          positionsRes?.success && Array.isArray(positionsRes.data)
            ? positionsRes.data
            : [];

        setAgents(allAgents);
        setPositions(positionsData);

        await fetchPitches(filters, {
          showLoader: false,
          agentsSource: allAgents,
        });
      } catch (error) {
        console.error("Failed to fetch initial agency data:", error);
      } finally {
        if (!isCancelled) {
          setPitchesLoading(false);
          setInitialLoadDone(true);
        }
      }
    };

    fetchInitialData();

    return () => {
      isCancelled = true;
    };
  }, []);

  // Handle debounced search
  useEffect(() => {
    if (!initialLoadDone || (!hasSearchInteracted && debouncedSearch === "")) {
      return;
    }

    const requestFilters = {
      ...filters,
      search: debouncedSearch,
      page: 1,
    };

    fetchPitches(requestFilters);
    setFilters((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearch, initialLoadDone, hasSearchInteracted]);

  // Filter handlers
  const handleSearchChange = useCallback((value) => {
    setHasSearchInteracted(true);
    setFilters((prev) => ({ ...prev, search: value }));
  }, []);

  const handleAgentToggle = useCallback(
    (agentId) => {
      setFilters((prev) => {
        const isSelected = prev.selectedAgents.includes(agentId);
        const updatedSelectedAgents = isSelected
          ? prev.selectedAgents.filter((id) => id !== agentId)
          : [...prev.selectedAgents, agentId];

        const updatedFilters = {
          ...prev,
          selectedAgents: updatedSelectedAgents,
          allAgentsSelected: updatedSelectedAgents.length === 0,
          page: 1,
        };

        fetchPitches(updatedFilters);
        return updatedFilters;
      });
    },
    [fetchPitches]
  );

  const handlePositionToggle = useCallback(
    (position) => {
      setFilters((prev) => {
        const isSelected = prev.selectedPositions.includes(position);
        const updatedSelectedPositions = isSelected
          ? prev.selectedPositions.filter((pos) => pos !== position)
          : [...prev.selectedPositions, position];

        const updatedFilters = {
          ...prev,
          selectedPositions: updatedSelectedPositions,
          allPositionsSelected: updatedSelectedPositions.length === 0,
          page: 1,
        };

        fetchPitches(updatedFilters);
        return updatedFilters;
      });
    },
    [fetchPitches]
  );

  const handleAllAgentsToggle = useCallback(() => {
    setFilters((prev) => {
      const nextAllAgentsSelected = !prev.allAgentsSelected;
      const updatedSelectedAgents = nextAllAgentsSelected
        ? []
        : agents.map((agent) => agent.id);

      const updatedFilters = {
        ...prev,
        allAgentsSelected: nextAllAgentsSelected,
        selectedAgents: updatedSelectedAgents,
        page: 1,
      };

      fetchPitches(updatedFilters);
      return updatedFilters;
    });
  }, [agents, fetchPitches]);

  const handleAllPositionsToggle = useCallback(() => {
    setFilters((prev) => {
      const nextAllPositionsSelected = !prev.allPositionsSelected;
      const updatedSelectedPositions = nextAllPositionsSelected
        ? []
        : positions.map((pos) => pos.id || pos.name);

      const updatedFilters = {
        ...prev,
        allPositionsSelected: nextAllPositionsSelected,
        selectedPositions: updatedSelectedPositions,
        page: 1,
      };

      fetchPitches(updatedFilters);
      return updatedFilters;
    });
  }, [positions, fetchPitches]);

  const handleSortChange = useCallback(
    (value) => {
      setFilters((prev) => {
        const updatedFilters = { ...prev, sort: value, page: 1 };
        fetchPitches(updatedFilters);
        return updatedFilters;
      });
    },
    [fetchPitches]
  );

  // Navigation handlers
  const handleAgentClick = useCallback(
    (agentId) => {
      navigate(`/search/agent/${agentId}`);
    },
    [navigate]
  );

  const handlePlayerClick = useCallback(
    (playerId) => {
      navigate(`/search/player/${playerId}`);
    },
    [navigate]
  );

  const handleClubClick = useCallback(
    (clubId) => {
      navigate(`/search/club/${clubId}`);
    },
    [navigate]
  );

  // Computed values
  const agentFilterText = filters.allAgentsSelected
    ? "All agents"
    : `${filters.selectedAgents.length} agent${
        filters.selectedAgents.length !== 1 ? "s" : ""
      }`;

  const positionFilterText = filters.allPositionsSelected
    ? "All positions"
    : `${filters.selectedPositions.length} position${
        filters.selectedPositions.length !== 1 ? "s" : ""
      }`;

  return {
    // Filter states
    filters,
    setFilters,
    showAgentFilter,
    setShowAgentFilter,
    showPositionFilter,
    setShowPositionFilter,

    // Data
    pitches,
    agents,
    positions,
    pitchesLoading,
    pagination,

    // Export state
    exportingData,
    setExportingData,

    // Filter handlers
    handleSearchChange,
    handleAgentToggle,
    handlePositionToggle,
    handleAllAgentsToggle,
    handleAllPositionsToggle,
    handleSortChange,

    // Navigation handlers
    handleAgentClick,
    handlePlayerClick,
    handleClubClick,

    // Computed values
    agentFilterText,
    positionFilterText,

    // Fetch functions
    fetchPitches,
  };
};

export default useAgencyPitches;
